import useSWR, { type SWRResponse } from "swr";
import type { CountryType, Nullable } from "../../../@types/generic";
import { useCountry } from "../../../hooks/useCountry";
import { QUERY_NAME_POLLEN_NEARBY } from "../../cacheKeys";
import { fetcher } from "../index";
import type { PollenNearby } from "../types/pollenNearby";

type Parameters = {
    latitude: Nullable<number> | undefined;
    longitude: Nullable<number> | undefined;
    enabled?: boolean;
};

const fetchPollenNearby = async (
    latitude: number,
    longitude: number,
    country: CountryType
): Promise<Nullable<PollenNearby>> =>
    fetcher<PollenNearby>(
        {
            path: `/pollenwcom/nearby/${latitude.toString()}/${longitude.toString()}/`,
        },
        { country }
    )
        .then(({ data }) => data)
        .catch(() => null);

export const usePollenNearby = ({
    latitude,
    longitude,
    enabled,
}: Parameters): SWRResponse<Nullable<PollenNearby>> => {
    const country = useCountry();
    const isEnabled =
        ((typeof enabled === "boolean" && enabled) ||
            typeof enabled === "undefined") &&
        typeof latitude === "number" &&
        typeof longitude === "number";

    const { data, isLoading, ...rest } = useSWR<Nullable<PollenNearby>>(
        isEnabled
            ? [QUERY_NAME_POLLEN_NEARBY, latitude, longitude, country]
            : null,
        () =>
            fetchPollenNearby(latitude as number, longitude as number, country)
    );

    return {
        data,
        isLoading: isLoading || data === undefined,
        ...rest,
    };
};

export const hasPollenForecast = (
    pollenNearby: Nullable<PollenNearby>
): pollenNearby is PollenNearby =>
    pollenNearby !== null &&
    pollenNearby.daily.some((day) => day.pollenForecast.length > 0);
